/**
 * ScriptToAudioPanel — Studio mode "Script → Audio only".
 *
 * A creator pastes / uploads a script, picks a language and voice, and
 * we generate a downloadable narration MP3 via the backend's edge-TTS
 * engine. Optional: also generate a static waveform PNG so the audio
 * can be fed back into pipelines (Opus etc.) that insist on a video.
 *
 * Endpoints used:
 *   POST /api/script-to-audio  (multipart form)
 *   GET  /api/languages        (existing — for the language picker)
 *
 * Voices are fetched from /api/languages which returns
 * {code -> {name, voices: [{id,label,gender}]}} for all 14 languages.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Music4, Loader2, Download, Upload, Sparkles, X, Volume2, Image as ImageIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_CHARS = 25000;

export default function ScriptToAudioPanel() {
  const [languages, setLanguages] = useState([]); // [{code,name}, …]
  const [voicesByLang, setVoicesByLang] = useState({}); // { code: [{id,label,gender}] }
  const [language, setLanguage] = useState("en");
  const [voice, setVoice] = useState("");
  const [text, setText] = useState("");
  const [wantWaveform, setWantWaveform] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pollState, setPollState] = useState(null); // { progress, stage } while running
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/languages`).then(({ data }) => {
      setLanguages(Array.isArray(data) ? data : []);
    });
    axios.get(`${API}/voices`).then(({ data }) => {
      setVoicesByLang(data || {});
    });
  }, []);

  // Reset the voice selection when the language changes so an invalid
  // combination can't linger silently.
  const voicesForLang = useMemo(
    () => voicesByLang?.[language] || [],
    [voicesByLang, language]
  );
  useEffect(() => {
    if (!voicesForLang.length) return;
    const ok = voicesForLang.some((v) => v.id === voice);
    if (!ok) setVoice(voicesForLang[0].id);
  }, [voicesForLang, voice]);

  const onFile = async (f) => {
    if (!f) return;
    if (!/\.(txt|md|srt)$/i.test(f.name)) {
      setError("Please upload a plain-text file (.txt, .md or .srt).");
      return;
    }
    try {
      const buf = await f.text();
      setText(buf.slice(0, MAX_CHARS));
      setError("");
    } catch (e) {
      setError(`Couldn't read file: ${e.message}`);
    }
  };

  const generate = async () => {
    setError("");
    if (text.trim().length < 4) {
      setError("Please paste or upload at least a sentence of narration.");
      return;
    }
    setGenerating(true);
    setResult(null);
    setPollState({ progress: 0, stage: "queued" });
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      fd.append("language", language);
      if (voice) fd.append("voice", voice);
      if (wantWaveform) fd.append("generate_waveform", "true");
      // Kick off the async job. `/start` returns immediately with a
      // job_id no matter how long the script is — Cloudflare-safe.
      const { data: startResp } = await axios.post(
        `${API}/script-to-audio/start`,
        fd,
        { timeout: 30000 }
      );
      const jobId = startResp.job_id;
      // Poll every 2 seconds until done|failed. 15 min of narration
      // typically renders in 60–120s so 900s / 2s = 450 polls is a
      // massively generous upper bound.
      const maxTicks = 450;
      let ticks = 0;
      while (ticks < maxTicks) {
        await new Promise((r) => setTimeout(r, 2000));
        ticks += 1;
        const { data: st } = await axios.get(
          `${API}/script-to-audio/${jobId}`,
          { timeout: 15000 }
        );
        setPollState({
          progress: st.progress || 0,
          stage: st.stage || "processing",
        });
        if (st.status === "done") {
          setResult(st);
          return;
        }
        if (st.status === "failed") {
          setError(st.error || "Voice generation failed");
          return;
        }
      }
      setError("Timed out waiting for the narration to render. It may still finish — refresh and check the /studio again.");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setGenerating(false);
      setPollState(null);
    }
  };

  const durationLabel = (secs) => {
    if (!secs) return "";
    const m = Math.floor(secs / 60);
    const s = Math.round(secs - m * 60);
    return `${m}m ${String(s).padStart(2, "0")}s`;
  };

  const sizeLabel = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1_000_000) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1_000_000).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="script-to-audio-panel">
      <div className="panel p-6 md:p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow mb-1">Script → Audio only</div>
            <div className="font-display text-xl leading-snug">
              Just the narration MP3 — no video
            </div>
            <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed max-w-xl">
              Great for feeding audio back into pipelines that cap script-to-video
              (like Opus at 3&nbsp;min). Uses the same cinematic Edge-TTS voices as
              the dub flow, in all 14 languages.
            </div>
          </div>
          <Music4 className="w-6 h-6 text-[color:var(--accent)] opacity-60" />
        </div>

        {/* Language + voice row */}
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="label-eyebrow mb-2 block">Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
              data-testid="s2a-language"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code} className="bg-black">
                  {l.name || l.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-2 block">Voice</label>
            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
              data-testid="s2a-voice"
            >
              {voicesForLang.map((v) => (
                <option key={v.id} value={v.id} className="bg-black">
                  {v.label}
                </option>
              ))}
              {voicesForLang.length === 0 && (
                <option value="" className="bg-black">
                  No voices for this language
                </option>
              )}
            </select>
          </div>
        </div>

        {/* Script input */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="label-eyebrow">Script</label>
            <div className="flex items-center gap-3 text-[10px] text-[color:var(--muted)]">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1 hover:text-white transition-colors"
                data-testid="s2a-upload-txt"
              >
                <Upload className="w-3 h-3" /> Upload .txt
              </button>
              <span>
                <span className={text.length > MAX_CHARS ? "text-rose-400" : ""}>
                  {text.length.toLocaleString()}
                </span>
                {" / "}
                {MAX_CHARS.toLocaleString()} chars
              </span>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.srt,text/plain"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            rows={10}
            placeholder={`Paste your narration script here…\n\nAny length up to ${MAX_CHARS.toLocaleString()} characters (roughly 60 minutes of narration).`}
            className="input-base w-full px-4 py-3 leading-relaxed resize-y"
            data-testid="s2a-script"
          />
        </div>

        {/* Options row */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <label className="inline-flex items-start gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={wantWaveform}
              onChange={(e) => setWantWaveform(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-[color:var(--accent)]"
              data-testid="s2a-waveform-toggle"
            />
            <span className="text-[color:var(--muted)]">
              Also generate a static waveform image (1920&times;1080 PNG) — useful
              as a &quot;video&quot; surrogate for Opus.
            </span>
          </label>
        </div>

        {error && (
          <div className="mb-4 panel p-3 border-red-500/40 text-red-300 text-sm" data-testid="s2a-error">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={generate}
            disabled={generating || text.trim().length < 4}
            className="btn-primary text-sm inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-40"
            data-testid="s2a-generate"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating narration…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate narration
              </>
            )}
          </button>
          {generating && (
            <div className="text-xs text-[color:var(--muted)]">
              {pollState
                ? `${pollState.stage.replace(/_/g, " ")}… ${pollState.progress}%`
                : "Starting up…"}
            </div>
          )}
        </div>
        {generating && pollState && pollState.progress > 0 && (
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-[color:var(--accent)] transition-all duration-500 ease-out"
                style={{ width: `${pollState.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Result panel */}
      {result && (
        <div className="panel p-6" data-testid="s2a-result">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="label-eyebrow mb-1">Ready to download</div>
              <div className="font-display text-lg">Your narration is ready</div>
              <div className="text-xs text-[color:var(--muted)] mt-1">
                <Volume2 className="w-3 h-3 inline mr-1" />
                {durationLabel(result.duration_seconds)} · {sizeLabel(result.size_bytes)} ·{" "}
                {result.language_name} · {result.character_count.toLocaleString()} chars
              </div>
            </div>
            <button
              onClick={() => setResult(null)}
              className="text-[color:var(--muted)] hover:text-white"
              aria-label="Dismiss result"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Inline audio player */}
          <audio
            controls
            src={`${BACKEND_URL}${result.audio_url}`}
            className="w-full mb-3"
            data-testid="s2a-audio-player"
          />

          <div className="flex flex-wrap gap-3">
            <a
              href={`${BACKEND_URL}${result.audio_url}`}
              download
              className="btn-primary text-xs inline-flex items-center gap-2 px-4 py-2"
              data-testid="s2a-download-mp3"
            >
              <Download className="w-3.5 h-3.5" />
              Download MP3
            </a>
            {result.waveform_url && (
              <a
                href={`${BACKEND_URL}${result.waveform_url}`}
                download
                className="text-xs inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
                data-testid="s2a-download-png"
              >
                <ImageIcon className="w-3.5 h-3.5" />
                Download waveform PNG
              </a>
            )}
          </div>

          <div className="mt-4 text-[11px] text-[color:var(--muted)] leading-relaxed">
            Tip: to use this with Opus, download the MP3 and the PNG, then upload
            them both to Opus as an <em>Audio + Image</em> project — Opus will treat
            the waveform as the video track and use the MP3 as the audio.
          </div>
        </div>
      )}
    </div>
  );
}
